import config from '@/appsettings.json';

export class ApiError extends Error {
  statusCode?: number;
  response?: unknown;
  constructor(message: string, statusCode?: number, response?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.response = response;
  }
}

const COMPANY_STORAGE_KEY = 'selectedCompanyId';

export function getRequestHeaders(): HeadersInit {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };

  // Using sessionStorage for security - prevents cross-session persistence of company selection
  const companyId = sessionStorage.getItem(COMPANY_STORAGE_KEY);
  if (companyId) {
    headers['X-Company-Id'] = companyId;
  }

  return headers;
}

export async function apiGet<T>(
  path: string,
  params?: Record<string, string | number | boolean | undefined>,
  signal?: AbortSignal,
): Promise<T> {
  const url = new URL(`${config.apiBaseUrl}${path}`);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, String(value));
      }
    });
  }

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: getRequestHeaders(),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
    }

    return await response.json();
  } catch (error: unknown) {
    // Let AbortError bubble so React Query can treat it as a cancellation
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
  }
}

export async function apiPut<T, TBody = unknown>(path: string, body?: TBody, signal?: AbortSignal): Promise<T> {
  const url = `${config.apiBaseUrl}${path}`;

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: getRequestHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
    }

    return await response.json();
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
  }
}

export async function apiDelete(path: string, signal?: AbortSignal): Promise<void> {
  const url = `${config.apiBaseUrl}${path}`;

  try {
    const response = await fetch(url, {
      method: 'DELETE',
      headers: getRequestHeaders(),
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
    }
  } catch (error: unknown) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
  }
}

export async function apiPost<T, TBody = unknown>(path: string, body?: TBody, signal?: AbortSignal): Promise<T> {
  const url = `${config.apiBaseUrl}${path}`;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: getRequestHeaders(),
      body: body ? JSON.stringify(body) : undefined,
      signal,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(errorData.message || `HTTP error ${response.status}`, response.status, errorData);
    }

    return await response.json();
  } catch (error: unknown) {
    // Let AbortError bubble so React Query can treat it as a cancellation
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
    if (error instanceof ApiError) {
      throw error;
    }
    throw new ApiError(error instanceof Error ? error.message : 'Network error occurred');
  }
}
